import React, { createContext, useContext, useReducer, useRef, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

interface PlayerState {
  currentTrack: Track | null;
  isPlaying: boolean;
  volume: number;
  queue: Track[];
  history: Track[];
  isPreviewMode: boolean;
  previewTimeRemaining: number;
  showSubscriptionPrompt: boolean;
  hasFullAccess: boolean;
}

interface Track {
  id: string;
  title: string;
  artist: string;
  artist_name?: string;
  album: string;
  coverArt: string;
  cover_art_url?: string;
  audioUrl: string;
  audio_url?: string;
  duration: number;
  price?: number;
  isPurchased?: boolean;
}

type PlayerAction =
  | { type: 'SET_TRACK'; payload: Track }
  | { type: 'PLAY' }
  | { type: 'PAUSE' }
  | { type: 'TOGGLE_PLAY' }
  | { type: 'SET_VOLUME'; payload: number }
  | { type: 'ADD_TO_QUEUE'; payload: Track }
  | { type: 'REMOVE_FROM_QUEUE'; payload: number }
  | { type: 'CLEAR_QUEUE' }
  | { type: 'NEXT_TRACK' }
  | { type: 'PREVIOUS_TRACK' }
  | { type: 'ADD_TO_HISTORY'; payload: Track }
  | { type: 'SET_PREVIEW_MODE'; payload: boolean }
  | { type: 'SET_PREVIEW_TIME_REMAINING'; payload: number }
  | { type: 'SHOW_SUBSCRIPTION_PROMPT'; payload: boolean }
  | { type: 'SET_FULL_ACCESS'; payload: boolean }
  | { type: 'SET_TRACK_PURCHASED'; payload: boolean };

const initialState: PlayerState = {
  currentTrack: null,
  isPlaying: false,
  volume: 0.7,
  queue: [],
  history: [],
  isPreviewMode: true,
  previewTimeRemaining: 30,
  showSubscriptionPrompt: false,
  hasFullAccess: false,
};

const PREVIEW_DURATION = 30;

const playerReducer = (state: PlayerState, action: PlayerAction): PlayerState => {
  switch (action.type) {
    case 'SET_TRACK':
      return {
        ...state,
        currentTrack: action.payload,
        isPlaying: true,
        previewTimeRemaining: PREVIEW_DURATION,
        showSubscriptionPrompt: false,
      };
    case 'PLAY':
      return {
        ...state,
        isPlaying: true,
      };
    case 'PAUSE':
      return {
        ...state,
        isPlaying: false,
      };
    case 'TOGGLE_PLAY':
      return {
        ...state,
        isPlaying: !state.isPlaying,
      };
    case 'SET_VOLUME':
      return {
        ...state,
        volume: action.payload,
      };
    case 'ADD_TO_QUEUE':
      return {
        ...state,
        queue: [...state.queue, action.payload],
      };
    case 'REMOVE_FROM_QUEUE':
      return {
        ...state,
        queue: state.queue.filter((_, i) => i !== action.payload),
      };
    case 'CLEAR_QUEUE':
      return {
        ...state,
        queue: [],
      };
    case 'NEXT_TRACK':
      if (state.queue.length === 0) return state;
      return {
        ...state,
        currentTrack: state.queue[0],
        queue: state.queue.slice(1),
        history: state.currentTrack ? [...state.history, state.currentTrack] : state.history,
        previewTimeRemaining: PREVIEW_DURATION,
        showSubscriptionPrompt: false,
      };
    case 'PREVIOUS_TRACK':
      if (state.history.length === 0) return state;
      return {
        ...state,
        currentTrack: state.history[state.history.length - 1],
        history: state.history.slice(0, -1),
        queue: state.currentTrack ? [state.currentTrack, ...state.queue] : state.queue,
        previewTimeRemaining: PREVIEW_DURATION,
        showSubscriptionPrompt: false,
      };
    case 'ADD_TO_HISTORY':
      return {
        ...state,
        history: [...state.history, action.payload],
      };
    case 'SET_PREVIEW_MODE':
      return {
        ...state,
        isPreviewMode: action.payload,
      };
    case 'SET_PREVIEW_TIME_REMAINING':
      return {
        ...state,
        previewTimeRemaining: action.payload,
      };
    case 'SHOW_SUBSCRIPTION_PROMPT':
      return {
        ...state,
        showSubscriptionPrompt: action.payload,
        isPlaying: action.payload ? false : state.isPlaying,
      };
    case 'SET_FULL_ACCESS':
      return {
        ...state,
        hasFullAccess: action.payload,
        isPreviewMode: !action.payload,
      };
    case 'SET_TRACK_PURCHASED':
      return {
        ...state,
        currentTrack: state.currentTrack
          ? { ...state.currentTrack, isPurchased: action.payload }
          : null,
      };
    default:
      return state;
  }
};

const PlayerContext = createContext<{
  state: PlayerState;
  dispatch: React.Dispatch<PlayerAction>;
  audioRef: React.RefObject<HTMLAudioElement>;
  checkContentAccess: (contentId: string) => Promise<boolean>;
} | null>(null);

export const PlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(playerReducer, initialState);
  const audioRef = useRef<HTMLAudioElement>(null);
  const playPromiseRef = useRef<Promise<void> | null>(null);
  const { user } = useAuth();
  const [subscriptionTier, setSubscriptionTier] = useState<string>('free');

  useEffect(() => {
    const checkSubscription = async () => {
      if (!user) {
        dispatch({ type: 'SET_FULL_ACCESS', payload: false });
        return;
      }

      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('subscription_tier, subscription_expires_at')
          .eq('id', user.id)
          .maybeSingle();

        if (profile) {
          const hasActiveSubscription =
            profile.subscription_tier === 'unlimited' &&
            (!profile.subscription_expires_at ||
              new Date(profile.subscription_expires_at) > new Date());

          setSubscriptionTier(profile.subscription_tier || 'free');
          dispatch({ type: 'SET_FULL_ACCESS', payload: hasActiveSubscription });
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
      }
    };

    checkSubscription();
  }, [user]);

  const checkContentAccess = async (contentId: string): Promise<boolean> => {
    if (state.hasFullAccess) return true;
    if (!user) return false;

    try {
      const { data } = await supabase
        .from('user_purchases')
        .select('id')
        .eq('user_id', user.id)
        .eq('content_type', 'track')
        .eq('content_id', contentId)
        .eq('status', 'completed')
        .maybeSingle();

      return !!data;
    } catch (error) {
      console.error('Error checking content access:', error);
      return false;
    }
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = state.volume;
    }
  }, [state.volume]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = async () => {
      try {
        if (state.isPlaying && !state.showSubscriptionPrompt) {
          playPromiseRef.current = audio.play();
          await playPromiseRef.current;
        } else {
          if (playPromiseRef.current) {
            await playPromiseRef.current;
          }
          audio.pause();
        }
      } catch (error) {
        console.error('Playback error:', error);
      }
    };

    handlePlay();
  }, [state.isPlaying, state.currentTrack, state.showSubscriptionPrompt]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !state.currentTrack) return;

    const handleTimeUpdate = () => {
      const currentTime = audio.currentTime;

      if (!state.hasFullAccess && !state.currentTrack?.isPurchased) {
        const remaining = Math.max(0, PREVIEW_DURATION - currentTime);
        dispatch({ type: 'SET_PREVIEW_TIME_REMAINING', payload: remaining });

        if (currentTime >= PREVIEW_DURATION) {
          audio.pause();
          dispatch({ type: 'SHOW_SUBSCRIPTION_PROMPT', payload: true });
        }
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    return () => audio.removeEventListener('timeupdate', handleTimeUpdate);
  }, [state.currentTrack, state.hasFullAccess]);

  useEffect(() => {
    const checkTrackAccess = async () => {
      if (state.currentTrack && user) {
        const hasAccess = await checkContentAccess(state.currentTrack.id);
        dispatch({ type: 'SET_TRACK_PURCHASED', payload: hasAccess });
      }
    };

    checkTrackAccess();
  }, [state.currentTrack?.id, user]);

  return (
    <PlayerContext.Provider value={{ state, dispatch, audioRef, checkContentAccess }}>
      {children}
      <audio
        ref={audioRef}
        src={state.currentTrack?.audioUrl || state.currentTrack?.audio_url}
        onEnded={() => dispatch({ type: 'NEXT_TRACK' })}
      />
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
};
